import './polyfills'
import { isServer, MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { basename } from './lib/utils'
import { correlationHeaders } from './lib/client-trace'
import { documentOwnsPersistedCache, withSdkParams } from './lib/sdk-params'
import { resolveRedirectTarget } from './lib/redirect-target'
import { persistQueryClient, removeOldestQuery } from '@tanstack/react-query-persist-client'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import {
  createTRPCClient,
  httpBatchLink,
  httpBatchStreamLink,
  splitLink,
  TRPCClientError,
  type HTTPHeaders,
  type TRPCLink,
} from '@trpc/client'
import type { AppRouter } from 'server/trpc'
import type { inferRouterOutputs } from '@trpc/server'
import { trpcSSRCallFn } from './lib/trpc-server-fn'
import { shouldRetryQuery } from './lib/query-retry'
import { revalidateAfterWrite } from './lib/revalidate'
import { useAppStore } from './lib/app-store'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import { observable } from '@trpc/server/observable'
import { toast } from './lib/toast'
import { i18n, type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import type { ErrorCode } from 'shared'
import { classifyTrpcErrorToast, pickIncompleteTestPath } from './main-helpers'

/**
 * Shared client singletons (query client, tRPC client/proxy, persister). Import-safe on the server, which renders
 * the shell: no top-level `window` / `localStorage` access.
 */

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /**
       * Opt OUT of the global post-mutation revalidation (see the MutationCache below). Reserve it for writes
       * that provably cannot change anything the user could be looking at AND fire often enough that a full
       * revalidation per call would be pathological (the test's per-question autosave and its heartbeat).
       */
      invalidates?: false
    }
  }
}

// Factory so the server can mint a fresh client per request: a module singleton would leak one request's
// dehydrated cache into the next on the long-lived Bun server.
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    // On the caches, NOT a tRPC link, so they can be OBSERVER-GATED: a hover-preload prefetches the target route's
    // loader queries, and an error there has no observer, so a toast or redirect would fire "from nowhere".
    // Mutations have no render boundary, so they always surface.
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.getObserversCount() === 0) return
        handleClientTrpcError(error)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => handleClientTrpcError(error),
      // EVERY successful mutation revalidates the WHOLE cache (policy: lib/revalidate) — no per-mutation invalidation
      // lists to keep in sync with the backend's write fan-out. Fire-and-forget, and it runs BEFORE the mutation's own
      // callbacks: a call site that must hold its UI awaits `revalidateAfterWrite` itself in `onSuccess`.
      onSuccess: (_data, _variables, _onMutateResult, mutation, context) => {
        if (mutation.meta?.invalidates === false) return
        void revalidateAfterWrite(context.client)
      },
    }),
    defaultOptions: {
      queries: {
        // Client-only (lib/query-retry): on a lossy link a single dropped request would fail a query permanently. SSR
        // keeps no-retry so a render never blocks on backoff; mutations are untouched (a retry could double-submit).
        retry: isServer ? false : shouldRetryQuery,
        // 0.5s → 1s → 2s, so a genuinely-down backend surfaces its error within seconds instead of hanging the UI.
        retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 4000),
        staleTime: 60 * 1000,
        // Bounded, not Infinity: report/team queries are keyed on the selected member subset, so browsing
        // members spawns a cache entry per combination — uncollected, those accumulated and slowed the app.
        gcTime: 5 * 60 * 1000,
        // NO `placeholderData: keepPreviousData` as a global default. It is PER-QUERY, so a screen reading several
        // queries tears, and it LIES to any consumer that does not check `isPlaceholderData` (three dialogs needed an
        // `id === props.id` guard). Opt in with `useDeferredValue`, which holds the whole committed tree instead.
      },
    },
  })
}

export const queryClient = makeQueryClient()

// Picks the first unfinished questionnaire so a TEST_INCOMPLETE redirect resumes where the user left off.
export function firstIncompleteTestPath(): string {
  const user = queryClient.getQueryData<{
    testPassed?: boolean | null
    testPassedValues?: boolean | null
    testPassedPersonality?: boolean | null
  } | null>(trpc.auth.getUser.queryKey())
  return pickIncompleteTestPath(user)
}

// Backend control-flow errors arrive as a stable machine code in the tRPC `message`; the .po catalogs own the
// wording. Keep this map in sync with `ErrorCode`.
const ERROR_TRANSLATIONS: Partial<Record<ErrorCode, MessageDescriptor>> = {
  INVALID_CREDENTIALS: msg`Invalid email or password`,
  NOT_YOUR_EMPLOYER: msg`This is not one of your employers`,
  INSUFFICIENT_PERMISSIONS: msg`You don't have permission to perform this action`,
  INTERFACE_SWITCH_UNAVAILABLE: msg`That view isn't available`,
  SUPER_ADMIN_ONLY: msg`This area is restricted to D'Anchiano super-admins`,
  OFFER_NOT_FOUND: msg`Offer not found`,
  APPLICANT_NOT_FOUND: msg`Applicant not found`,
  APPLICANT_ONLY: msg`This action is only available to professional accounts`,
  COMPANY_ONLY: msg`This action is only available to company accounts`,
  EMAIL_IN_USE: msg`This email has already been used`,
  EMAIL_REQUIRED: msg`An email address is required`,
  SOCIAL_ID_IN_USE: msg`This social account is already linked to another account`,
  TOKEN_INVALID: msg`This link is invalid or has expired`,
  ORG_NOT_FOUND: msg`Organization not found`,
  TEAM_NOT_FOUND: msg`Team not found`,
  TEAM_ALREADY_EXISTS: msg`A team with this name already exists`,
  MEMBER_ALREADY_ON_TEAM: msg`This member is already on the team`,
  MEMBER_NOT_ON_TEAM: msg`This member is not on the team`,
  COMPANY_NOT_FOUND: msg`Company not found`,
  CUSTOMER_NOT_FOUND: msg`Customer not found`,
  INVALID_PRODUCT: msg`Invalid or unavailable product`,
  PAYMENT_UNAVAILABLE: msg`Payments are temporarily unavailable. Please try again.`,
  APP_NOT_FOUND: msg`Application not found`,
  PROFILE_BLOCKED: msg`This profile isn't available`,
  PROFILE_NOT_DEFINED: msg`No profile selected`,
  TEST_NOT_PASSED: msg`The psychographic test isn't completed yet`,
  INVITATION_INVALID: msg`This invitation is invalid or has expired`,
  TEAMS_REEVALUATE_FORBIDDEN: msg`You don't have permission to re-evaluate this member`,
  DOMAIN_MISMATCH: msg`This request isn't allowed from this domain`,
}

// Wired into the query and mutation caches rather than a tRPC link, so the query path can be observer-gated.
// Browser only — SSR query errors go to the router error boundary, and `window` is undefined there.
function handleClientTrpcError(error: unknown): void {
  if (typeof window === 'undefined') return
  const err = error as { message?: string; data?: { code?: string } }
  const code = (err.message ?? '') as ErrorCode
  const descriptor = ERROR_TRANSLATIONS[code]
  const action = classifyTrpcErrorToast({ code, dataCode: err.data?.code, hasTranslation: descriptor != null })
  if (action === 'redirect-login') {
    // INVALID_CREDENTIALS is excluded so a failed login keeps its in-place message. resolveRedirectTarget unwraps
    // the current URL, so firing this while already on /login reuses the real destination.
    const target = resolveRedirectTarget(window.location.pathname + window.location.search)
    const query = target ? `?redirect=${encodeURIComponent(target)}` : ''
    window.location.href = withSdkParams(`${basename}/login${query}`)
    return
  }
  if (action === 'redirect-test') {
    // Guard against a loop while already on a /test route (those pages only call ungated test/auth endpoints).
    if (!window.location.pathname.includes('/test')) {
      window.location.href = withSdkParams(`${basename}${firstIncompleteTestPath()}`)
    }
    return
  }
  if (action === 'redirect-report') {
    // A self-registered professional reached a company/employee-only section → their own profile.
    if (!window.location.pathname.includes('/report')) {
      window.location.href = withSdkParams(`${basename}/report`)
    }
    return
  }
  if (action === 'redirect-company') {
    // The inverse: an employee in company view hit their own /report page → the company home.
    if (!window.location.pathname.includes('/jobs')) {
      window.location.href = withSdkParams(`${basename}/jobs`)
    }
    return
  }
  if (action === 'toast-generic') {
    // A 500's detail (raw SQL / stack) is redacted by the server, so show a generic toast.
    toast.error(i18n._(msg`Something went wrong. Please try again.`))
    return
  }
  // toast-translated → render the known code's translation; toast-raw → surface the raw message.
  toast.error(action === 'toast-translated' && descriptor ? i18n._(descriptor) : (err.message ?? ''))
}

// Terminating link for SSR: dispatches through `trpcSSRCallFn`, a plain direct
// call that runs the procedure in-process against `appRouter` (no server-function RPC — see the
// profile notes in lib/trpc-server-fn.ts). It is wrapped in `createServerOnlyFn`, so the start
// compiler erases its body AND the `server/*` imports from the client bundle, leaving a throwing stub
// that is never reached: this link is only wired into the chain when `isServer` (switch below).
const ssrLink: TRPCLink<AppRouter> = () => {
  return ({ op }) => {
    return observable((observer) => {
      const type = op.type === 'subscription' ? 'query' : op.type
      trpcSSRCallFn({ path: op.path, input: op.input, type })
        .then((result) => {
          observer.next({ result: { type: 'data', data: result } })
          observer.complete()
        })
        .catch((err: unknown) => {
          observer.error(
            err instanceof TRPCClientError
              ? err
              : new TRPCClientError<AppRouter>(err instanceof Error ? err.message : String(err), {
                  cause: err instanceof Error ? err : undefined,
                }),
          )
        })
    })
  }
}

// Resolved once, lazily: `headers()` below runs on every tRPC call. `null` = resolved but unavailable.
let cachedTimezone: string | null | undefined
function resolveTimezone(): string | null {
  if (cachedTimezone === undefined) {
    try {
      cachedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null
    } catch {
      cachedTimezone = null
    }
  }
  return cachedTimezone
}

// ONE options object, so both links hit the same `/trpc` endpoint with the same auth/locale headers — only their
// batching and streaming behaviour differs.
const httpLinkOptions = {
  url: `${basename}/trpc`,
  headers() {
    const headers: HTTPHeaders = {}

    const { clientId: storeClientId, locale, userToken: storeUserToken } = useAppStore.getState().store
    const clientId = new URLSearchParams(location.search).get('clientId') ?? storeClientId
    // Fall back to the store (captured at load), so the token survives a navigation that drops it from the URL.
    const userToken = new URLSearchParams(location.search).get('token') ?? storeUserToken

    if (clientId) headers['Client-id'] = clientId
    if (userToken) headers['Authorization'] = `Bearer ${userToken}`
    // The slot the server checks before the `language` cookie; the store already resolved `?lang=` / lang / cookie.
    if (locale) headers['x-locale'] = locale

    if (typeof window !== 'undefined') {
      if (window.document.referrer) {
        const pathArray = window.document.referrer.split('/')
        const origin = `${pathArray[0]}//${pathArray[2]}`
        headers['Iframe-origin'] = origin
      } else {
        const pathArray = window.location.href.split('/')
        const origin = `${pathArray[0]}//${pathArray[2]}`
        headers['Iframe-origin'] = origin
      }

      // Wide-events correlation, so this request, the page render, any client error and the session timeline join one
      // trace (see wide-events/hono.ts). All best-effort; empty ids are omitted.
      Object.assign(headers, correlationHeaders())
      const tz = resolveTimezone()
      if (tz) headers['x-timezone'] = tz
      headers['x-viewport'] = `${String(window.innerWidth)}x${String(window.innerHeight)}`
      if (window.APP_VERSION) headers['x-app-version'] = window.APP_VERSION
    }

    return headers
  },
}

// Fetch a query imperatively WITHOUT publishing into the react-query cache (company registration writes the
// fresh user inside a view transition, so the auth-card → wizard box morphs deterministically).
export const trpcClient = createTRPCClient<AppRouter>({
  // The window-coupled `headers()` below would crash on the server, whose only consumers are the `/_app`
  // loaders and components — so it uses the in-process `ssrLink` exclusively.
  links: isServer
    ? [ssrLink]
    : [
        // Mutations set the auth session cookie. The streaming link holds the response connection open for batching, so
        // the post-login client navigation aborted it before the browser committed the Set-Cookie and the session was
        // silently dropped — route MUTATIONS through the non-streaming httpBatchLink. Queries keep the streaming one.
        splitLink({
          condition: (op) => op.type === 'mutation',
          true: httpBatchLink(httpLinkOptions),
          false: httpBatchStreamLink(httpLinkOptions),
        }),
      ],
})

export const trpc: ReturnType<typeof createTRPCOptionsProxy<AppRouter>> = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
})

export type User = NonNullable<inferRouterOutputs<AppRouter>['auth']['getUser']>

/**
 * May this document read/write the origin's persisted query cache? False for a token-scoped or iframe-embedded
 * document, which authenticates as a different entity than the session that owns the blob. Read ONCE at module
 * scope: the SDK params can only enter on a document load, and a value that flipped later would make the
 * persister's behaviour depend on when a write happened to fire.
 */
export const OWNS_PERSISTED_CACHE =
  typeof window !== 'undefined' && documentOwnsPersistedCache(window.location.search, window.self === window.top)

// Gating the STORAGE and not the provider is deliberate: `router.tsx` keeps rendering PersistQueryClientProvider
// either way, so the React tree stays identical to the server's and nothing can mismatch.
export const persister = createAsyncStoragePersister({
  storage: OWNS_PERSISTED_CACHE ? window.localStorage : undefined,
  retry: removeOldestQuery,
})

// Bump when a query's cached shape changes incompatibly, or when a release must start from a clean cache.
export const PERSIST_BUSTER = 'v2'

// Shared by the eager `persistQueryClient` and the `PersistQueryClientProvider` in router.tsx, so both agree.
export const persistOptions = {
  persister,
  buster: PERSIST_BUSTER,
}

export const clearQueryClient = () => {
  queryClient.clear()
  // Removed directly because the persister takes a second to sync. Only for the document that OWNS the blob: a
  // login or logout inside a token embed must not delete the surrounding owner session's cache.
  if (OWNS_PERSISTED_CACHE) window.localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE')
}

// Records WHICH account the persisted cache belongs to: social-OAuth login lands via a full document load that
// REHYDRATES the blob instead of clearing it, so __root.tsx compares this tag against the resolved getUser id and
// purges on a mismatch. Kept OUT of REACT_QUERY_OFFLINE_CACHE so it survives clearQueryClient().
export const PERSISTED_IDENTITY_KEY = 'dc-persisted-user-id'

// TODO: We're calling it also here instead of relying only on <PersistQueryClientProvider />
// because on quick reload is still not restoring from persister
if (typeof window !== 'undefined') {
  persistQueryClient({ queryClient, ...persistOptions })
}
